/**
 * Size guides: reusable measurement tables and optional "how to measure" content.
 * Used by admin API, storefront payload (ProductSizeGuidePayload), and DB alignment.
 */

export enum SizeGuideStatus {
  DRAFT = "DRAFT",
  PUBLIC = "PUBLIC",
}

export enum SizeGuideUnitBase {
  CM = "cm",
  IN = "in",
}

export enum SizeGuideUnitDisplayPolicy {
  AUTO = "AUTO",
  FIXED = "FIXED",
}

export interface SizeGuideTableColumn {
  id: string;
  label: string;
  order: number;
}

export interface SizeGuideTableRow {
  id: string;
  /** Maps column id -> cell display value (may include ranges or *literal* markers). */
  cells: Record<string, string>;
  order: number;
}

export interface SizeGuideTableJson {
  columns: SizeGuideTableColumn[];
  rows: SizeGuideTableRow[];
}

export interface SizeGuide {
  id: string;
  accountId: string;
  name: string;
  status: SizeGuideStatus;
  tableJson: SizeGuideTableJson;
  infoTitle?: string | null;
  infoBody?: string | null;
  infoMediaId?: string | null;
  unitBase: SizeGuideUnitBase;
  unitDisplayPolicy?: SizeGuideUnitDisplayPolicy | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
}

/** Row in size_guide_category (category-scoped rule with priority). */
export interface SizeGuideCategoryRule {
  sizeGuideId: string;
  categoryId: string;
  priority: number;
}

/** Assignment returned by admin API (without repeating sizeGuideId on each row). */
export interface SizeGuideCategoryAssignment {
  categoryId: string;
  priority: number;
}

export type SizeGuideDetail = SizeGuide & {
  categoryRules?: SizeGuideCategoryAssignment[];
};

export interface CreateSizeGuideDTO {
  accountId: string;
  name: string;
  status?: SizeGuideStatus;
  tableJson: SizeGuideTableJson;
  infoTitle?: string | null;
  infoBody?: string | null;
  infoMediaId?: string | null;
  unitBase?: SizeGuideUnitBase;
  unitDisplayPolicy?: SizeGuideUnitDisplayPolicy | null;
}

export interface UpdateSizeGuideDTO {
  name?: string;
  status?: SizeGuideStatus;
  tableJson?: SizeGuideTableJson;
  infoTitle?: string | null;
  infoBody?: string | null;
  infoMediaId?: string | null;
  unitBase?: SizeGuideUnitBase;
  unitDisplayPolicy?: SizeGuideUnitDisplayPolicy | null;
}

export interface SizeGuideCategoryRuleInput {
  categoryId: string;
  priority: number;
}

/** Resolved size guide on product detail (api-public / storefront). */
export interface ProductSizeGuidePayload {
  id: string;
  name: string;
  table: SizeGuideTableJson;
  unitBase: SizeGuideUnitBase;
  unitDisplayPolicy?: SizeGuideUnitDisplayPolicy | null;
  infoTitle?: string | null;
  infoBody?: string | null;
  infoImageUrl?: string | null;
}
